/* ═══════════════════════════════════════════════════════════════
   REBELS SHOP — THEME JS (v2)
   Vanilla JS, single file, no bundler (see README.md for rationale).
═══════════════════════════════════════════════════════════════ */

/* ── Shared utilities ──────────────────────────────────────────*/

var FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/* Loads an image off-screen and reports back — done(true) once it can be
   painted without a visible load, done(false) if it failed. */
function preloadImage(url, done) {
  var img = new Image();
  img.onload = function () { if (done) done(true); };
  img.onerror = function () { if (done) done(false); };
  img.src = url;
  return img;
}

/* The page scrolls inside #windowInner, NOT the document (see theme.css §6).
   Locking `document.body` — which is what this used to do — therefore did
   nothing at all: the page kept scrolling behind every open drawer. */
function scrollContainer() {
  return document.getElementById('windowInner') || document.documentElement;
}

var scrollLockCount = 0;
var scrollLockTop = 0;

function lockScroll() {
  var el = scrollContainer();
  if (scrollLockCount === 0 && el) {
    scrollLockTop = el.scrollTop;
    el.style.overflowY = 'hidden';
  }
  scrollLockCount++;
}

function unlockScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  var el = scrollContainer();
  if (scrollLockCount === 0 && el) {
    el.style.overflowY = '';
    el.scrollTop = scrollLockTop;
  }
}

/* Shopify publishes its route roots on window.Shopify (via content_for_header).
   Using them keeps every fetch below correct on locale-prefixed storefronts
   (/de/...), where a hardcoded '/' would silently drop the locale. */
function rootUrl() {
  var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return r || '/';
}

function sectionUrl(sectionId, path) {
  var base = path || rootUrl();
  return base + (base.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
}

/* ── Money ─────────────────────────────────────────────────────
   This theme's rule is that money is never formatted in JS: every price on
   the page comes out of Liquid's `money` filter already formatted (README,
   "JS architecture"). These two helpers are the one deliberate exception,
   and they exist for the single number that CANNOT be pre-rendered — the
   variant price plus whatever the product-options app is charging for the
   shopper's current selection, which is only known in the browser.

   The rule's real point is upheld: neither helper hardcodes a locale (the
   bug that produced the rule — "1.234,56 €" re-rendered as "€1.234,56").
   Both are driven by the merchant's own `shop.money_format`, handed to JS
   in data-money-format on [data-price-row]. Add no other callers. */
var MONEY_STYLES = {
  amount: { thousands: ',', decimal: '.', places: 2 },
  amount_no_decimals: { thousands: ',', decimal: '.', places: 0 },
  amount_with_comma_separator: { thousands: '.', decimal: ',', places: 2 },
  amount_no_decimals_with_comma_separator: { thousands: '.', decimal: ',', places: 0 },
  amount_with_apostrophe_separator: { thousands: "'", decimal: '.', places: 2 },
  amount_with_space_separator: { thousands: ' ', decimal: ',', places: 2 },
  amount_no_decimals_with_space_separator: { thousands: ' ', decimal: ',', places: 0 },
  amount_with_period_and_space_separator: { thousands: ' ', decimal: '.', places: 2 },
};

/* Which of `.` and `,` is the decimal mark is decided by the placeholder
   the merchant's format uses, never guessed from the digits — "€1.234" is
   1234 in a German shop and 1.234 nowhere. */
function moneyStyle(format) {
  var match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
  return (match && MONEY_STYLES[match[1]]) || MONEY_STYLES.amount;
}

function formatMoney(cents, format) {
  var style = moneyStyle(format);
  var abs = Math.round(Math.abs(cents));
  var units = style.places === 0 ? Math.round(abs / 100) : Math.floor(abs / 100);
  var amount = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, style.thousands);
  if (style.places > 0) amount += style.decimal + ('0' + (abs % 100)).slice(-2);
  var body = (format || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  return cents < 0 ? '-' + body : body;
}

/* The inverse, for reading a price back out of an app's rendered DOM —
   " (+ €10,00)" -> 1000. Takes the format the *writer* used, which is not
   necessarily this theme's: the options app carries its own copy of the
   money format (window.fpob_settings.moneyFormat). */
function parseMoney(text, format) {
  var style = moneyStyle(format);
  var str = String(text == null ? '' : text);
  var firstDigit = str.search(/\d/);
  if (firstDigit === -1) return 0;

  // A leading minus can be separated from the digits by the currency symbol
  // and spaces ("- €10,00"), so look at everything ahead of the number.
  var negative = str.slice(0, firstDigit).indexOf('-') !== -1;
  var body = str.slice(firstDigit).replace(/[^\d.,'\s\u00A0].*$/, '');

  var cut = body.lastIndexOf(style.decimal);
  var whole = cut === -1 ? body : body.slice(0, cut);
  var frac = cut === -1 ? '' : body.slice(cut + 1);
  // Three digits after the separator means it was a thousands separator
  // after all (a no-decimals shop rendering "1.234"), not a decimal mark.
  if (!/^\d{1,2}$/.test(frac)) { whole = body; frac = ''; }

  var cents = parseInt(whole.replace(/\D/g, '') || '0', 10) * 100 +
    parseInt((frac + '00').slice(0, 2), 10);
  return negative ? -cents : cents;
}

/* Fired on `document` by initAppOptions whenever the options app's
   surcharge total changes; initProductPage folds it into the displayed
   price. An event rather than a direct call because either function may
   be absent (auction pages have no price element; most pages have no
   options app) and neither should have to know about the other. */
var OPTION_PRICE_EVENT = 'rebels:option-price-change';

/* One overlay implementation for the cart drawer, the mobile menu and the
   mobile filter drawer. Each previously had its own ad-hoc open/close with
   no Escape handling, no focus trap and no focus restoration. */
function createOverlay(options) {
  var panel = options.panel;
  if (!panel) return null;

  var isOpen = false;
  var lastFocused = null;

  function onKeydown(e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];
    // Focus parked where Tab can't cycle from — outside the panel (<body>
    // after a click), or on a tabindex=-1 element in it like the lightbox's
    // stage — would otherwise let a Tab walk straight out of the dialog.
    var active = document.activeElement;
    var parked = items.indexOf(active) === -1 &&
      (!panel.contains(active) || active.getAttribute('tabindex') === '-1');
    if (parked) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    panel.classList.add('is-open');
    panel.removeAttribute('inert');
    if (options.overlayEl) options.overlayEl.classList.add('is-open');
    if (options.trigger) options.trigger.setAttribute('aria-expanded', 'true');
    if (options.lockScroll !== false) lockScroll();
    document.addEventListener('keydown', onKeydown);
    if (options.onOpen) options.onOpen(panel);
    focusFirst();
  }

  function focusFirst() {
    var target = options.initialFocus && panel.querySelector(options.initialFocus);
    if (!target) target = panel.querySelector(FOCUSABLE);
    if (target) {
      // rAF so the element is laid out (and focusable) before we focus it
      requestAnimationFrame(function () { target.focus(); });
    }
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    panel.classList.remove('is-open');
    if (options.overlayEl) options.overlayEl.classList.remove('is-open');
    if (options.trigger) options.trigger.setAttribute('aria-expanded', 'false');
    if (options.lockScroll !== false) unlockScroll();
    document.removeEventListener('keydown', onKeydown);
    // inert keeps the closed panel out of the tab order. Without it the cart
    // drawer — which only slides away via transform — stayed keyboard
    // reachable while invisible.
    panel.setAttribute('inert', '');
    if (options.onClose) options.onClose(panel);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  /* Scrims are driven by opacity + pointer-events (see theme.css), NOT by
     display or the hidden attribute — toggling `hidden` here was either a
     no-op (the filters scrim's media query sets display:block, which wins
     over [hidden]) or actively harmful (going display:none -> visible in one
     frame skips the fade). They hold no focusable content, so leaving them
     in the layer is fine. */
  if (options.overlayEl) options.overlayEl.addEventListener('click', close);
  if (!panel.classList.contains('is-open')) panel.setAttribute('inert', '');

  return {
    open: open,
    close: close,
    toggle: function () { isOpen ? close() : open(); },
    isOpen: function () { return isOpen; },
  };
}

/* ── Boot ──────────────────────────────────────────────────────*/

document.addEventListener('DOMContentLoaded', function () {
  initOuterLock();
  initDarkMode();
  initMobileMenu();
  initFooterAccordion();
  initHeroCarousel();
  initProductPage();
  initStickyBuyBar();
  initCartDrawer();
  initCartPage();
  initCollectionFilters();
  initAddressForms();
  initAccordions();
  initProductRecommendations();
  initSearchPanel();
  initAppOptions();
  initAuctionNotices();
});

/* ── Outer window lock ─────────────────────────────────────────
   Only #windowInner scrolls; the document around the framed window must sit
   at 0,0 (theme.css §6, "second rule"). CSS already gives the document
   nothing to scroll — html/body are 100dvh. What CSS can't stop is iOS
   itself moving the layout viewport: focusing a field shifts the page up to
   clear the keyboard, and after the keyboard closes the shift sometimes
   stays, leaving the header half under the status bar.

   So: whenever the document is found offset, no text field is focused (the
   keyboard's shift is legitimate while one is), and the page isn't
   pinch-zoomed (panning a zoomed page is the shopper's own doing), put it
   back. Cheap — it reads two numbers, and only ever writes when they're off. */
function initOuterLock() {
  var vv = window.visualViewport;
  var queued = false;

  function editing() {
    var el = document.activeElement;
    if (!el || el === document.body) return false;
    if (el.isContentEditable) return true;
    if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
    return el.tagName === 'INPUT' && !/^(button|submit|reset|checkbox|radio|range|color|file|image)$/i.test(el.type);
  }

  function check() {
    queued = false;
    if (!window.scrollX && !window.scrollY) return;
    if (editing()) return;
    if (vv && vv.scale > 1.01) return;
    window.scrollTo(0, 0);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    // A beat later than the event: iOS finishes its own keyboard-close
    // scroll after focusout fires, and correcting mid-animation fights it.
    setTimeout(check, 120);
  }

  window.addEventListener('scroll', schedule, { passive: true });
  document.addEventListener('focusout', schedule);
  if (vv) vv.addEventListener('resize', schedule);
}

/* ── Dark mode ──────────────────────────────────────────────── */
function initDarkMode() {
  var toggles = document.querySelectorAll('[data-theme-toggle]');

  var themeColor = document.querySelector('meta[name="theme-color"]');

  function sync(mode) {
    toggles.forEach(function (btn) {
      // aria-pressed was rendered once server-side and then never updated,
      // so it always announced "not pressed" regardless of the real theme.
      btn.setAttribute('aria-pressed', mode === 'dark' ? 'true' : 'false');
    });
    // The browser chrome takes the page background (--bg) of the active
    // theme, so the bar above the page is never a different shade from it.
    if (themeColor) {
      themeColor.setAttribute('content', themeColor.getAttribute(mode === 'dark' ? 'data-dark' : 'data-light'));
    }
  }

  sync(document.documentElement.getAttribute('data-theme'));

  toggles.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try { localStorage.setItem('rebels-theme-pref', next); } catch (e) {}
      sync(next);
    });
  });
}

/* ── Mobile nav ────────────────────────────────────────────────*/
function initMobileMenu() {
  var menu = document.querySelector('[data-mobile-menu]');
  var toggle = document.querySelector('[data-mobile-menu-toggle]');
  if (!menu || !toggle) return;

  var overlay = createOverlay({
    panel: menu,
    trigger: toggle,
    // The WordPress site's dropdown panel (theme.css §7), not a sheet: the
    // page stays in view below it, so locking it would be surprising.
    lockScroll: false,
    onOpen: function () { toggle.classList.add('is-open'); },
    onClose: function () { toggle.classList.remove('is-open'); },
  });
  if (!overlay) return;

  // The panel only exists up to 900px. Rotating or resizing past that with it
  // open would leave it "open" (aria-expanded, focus trap) but invisible.
  var desktop = window.matchMedia ? window.matchMedia('(min-width: 901px)') : null;
  if (desktop) {
    var onBreakpoint = function (e) { if (e.matches) overlay.close(); };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    else if (desktop.addListener) desktop.addListener(onBreakpoint);
  }

  toggle.addEventListener('click', function () { overlay.toggle(); });
  menu.querySelectorAll('[data-mobile-menu-close]').forEach(function (el) {
    el.addEventListener('click', function () { overlay.close(); });
  });

  // Clicking anywhere outside the panel (or its trigger) dismisses it.
  document.addEventListener('click', function (e) {
    if (!overlay.isOpen()) return;
    if (menu.contains(e.target) || toggle.contains(e.target)) return;
    overlay.close();
  });

  menu.querySelectorAll('[data-mobile-submenu-toggle]').forEach(function (btn) {
    var submenu = btn.nextElementSibling;
    btn.addEventListener('click', function () {
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (!submenu) return;
      submenu.classList.toggle('is-open', !isOpen);
      if (isOpen) submenu.setAttribute('inert', '');
      else submenu.removeAttribute('inert');
    });
  });
}

/* ── Footer accordion (mobile only, CSS gates the breakpoint) ── */
function initFooterAccordion() {
  document.querySelectorAll('[data-footer-col-toggle]').forEach(function (toggle) {
    toggle.addEventListener('click', function () {
      var col = toggle.closest('[data-footer-col]');
      if (!col) return;
      var open = col.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });
}

/* ── Hero carousel ─────────────────────────────────────────────*/
function initHeroCarousel() {
  // querySelectorAll, not querySelector: the section has a preset, so a
  // merchant can drop a second hero on a page and the old code left it dead.
  document.querySelectorAll('[data-hero-carousel]').forEach(setUpHeroCarousel);
}

function setUpHeroCarousel(root) {
  var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
  var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
  var prevBtn = root.querySelector('[data-hero-prev]');
  var nextBtn = root.querySelector('[data-hero-next]');
  if (slides.length < 2) return;

  var toggleEl = root.querySelector('[data-hero-toggle]');
  var counterEl = root.querySelector('[data-hero-counter]');
  var index = 0;
  var reduced = prefersReducedMotion();
  var autoplay = root.dataset.autoplay === 'true' && !reduced;
  var speed = (parseInt(root.dataset.speed, 10) || 6) * 1000;
  var timer = null;
  // setTimeout, not setInterval, plus the time left on the current slide.
  // Pausing has to bank the remainder: an interval can only be cleared and
  // re-armed from zero, which restarted the visible countdown every time the
  // pointer crossed the hero — the bar appeared to reset at random.
  var remaining = speed;
  var startedAt = 0;

  // Reduced motion is decided here, not in the section, so the pause control
  // has to be withdrawn here too: the markup for it is rendered whenever the
  // merchant enables autoplay, but with autoplay actually off it would be a
  // button that pauses nothing. Removing the node also reverts the dot to a
  // plain bar, because the chip styling is gated on :has([data-hero-toggle]).
  if (!autoplay && toggleEl && toggleEl.parentNode) {
    toggleEl.parentNode.removeChild(toggleEl);
    toggleEl = null;
  }
  // The dot's own page label, kept so it can be restored when the dot stops
  // being the active one and hands the pause control on to its neighbour.
  dots.forEach(function (d) { d.dataset.pageLabel = d.getAttribute('aria-label') || ''; });

  /* ── Pause state ────────────────────────────────────────────
     A set of reasons, not one boolean. Four independent things pause this
     carousel — a held pointer, the pause button, focus inside a slide, and a
     hidden tab — and with a shared flag whichever of them ended LAST won:
     releasing a press while the tab was still hidden restarted autoplay in
     the background, and pressing the pause button while the CTA had focus
     un-paused it a moment later on focusout. Autoplay runs only when no
     reason is outstanding. */
  var pauseReasons = {};
  function isPaused() {
    for (var key in pauseReasons) { if (pauseReasons[key]) return true; }
    return false;
  }
  function addPause(reason) {
    if (pauseReasons[reason]) return;
    var was = isPaused();
    pauseReasons[reason] = true;
    if (!was) {
      // CSS freezes the countdown mid-sweep; bank the matching remainder so
      // the timer resumes at exactly the point the fill does.
      root.classList.add('is-paused');
      if (timer) {
        clearTimeout(timer);
        timer = null;
        remaining = Math.max(0, remaining - (Date.now() - startedAt));
      }
    }
    syncToggle();
  }
  function removePause(reason) {
    if (!pauseReasons[reason]) return;
    delete pauseReasons[reason];
    if (!isPaused()) {
      root.classList.remove('is-paused');
      armTimer();
    }
    syncToggle();
  }
  // The visible chip is driven entirely by CSS off .is-paused; this is only
  // the accessible name. It has to be set here rather than left to the chip's
  // own text, because that text is a state report ("paused") shown in one
  // state only, where the button needs a name describing its action in both.
  function syncToggle() {
    if (!toggleEl) return;
    var dot = dots[index];
    if (!dot) return;
    var stopped = !!pauseReasons.toggle;
    dot.setAttribute('aria-pressed', stopped ? 'true' : 'false');
    dot.setAttribute('aria-label', stopped ? toggleEl.dataset.labelPlay : toggleEl.dataset.labelPause);
  }

  function goTo(i) {
    index = (i + slides.length) % slides.length;
    slides.forEach(function (s, n) {
      var active = n === index;
      s.classList.toggle('is-active', active);
      // Off-screen slides kept their links in the tab order, so keyboard
      // users tabbed through invisible CTAs for every hidden slide.
      if (active) s.removeAttribute('inert');
      else s.setAttribute('inert', '');
      s.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    dots.forEach(function (d, n) {
      var active = n === index;
      d.classList.toggle('is-active', active);
      d.setAttribute('aria-current', active ? 'true' : 'false');
      // A dot is only a pause button while it holds the control. Handing it
      // on has to hand back the page label and drop aria-pressed, or the row
      // ends up with several buttons all claiming to be the pause toggle.
      if (!active) {
        d.removeAttribute('aria-pressed');
        if (d.dataset.pageLabel) d.setAttribute('aria-label', d.dataset.pageLabel);
      }
    });
    // One control, relocated — not one per dot. Eight copies of it would be
    // eight copies of the label for a screen reader to walk past, and eight
    // places for the paused state to fall out of sync.
    if (toggleEl && dots[index] && toggleEl.parentNode !== dots[index]) {
      dots[index].appendChild(toggleEl);
    }
    syncToggle();
    // The website's "01 / 05".
    if (counterEl) counterEl.textContent = pad2(index + 1) + ' / ' + pad2(slides.length);
    // A slide change is the only thing that legitimately zeroes the countdown.
    remaining = speed;
    restartProgress();
    armTimer();
  }
  // The active dot renders the countdown as a CSS animation on its ::after.
  // A pseudo-element animation can only be replayed by taking the class off
  // its host, forcing layout, and putting it back.
  function restartProgress() {
    if (!autoplay) return;
    root.classList.remove('is-autoplaying');
    void root.offsetWidth;
    root.classList.add('is-autoplaying');
  }
  function armTimer() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!autoplay || isPaused()) return;
    startedAt = Date.now();
    timer = setTimeout(next, remaining);
  }
  function next() { goTo(index + 1); }
  function prev() { goTo(index - 1); }
  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  if (nextBtn) nextBtn.addEventListener('click', next);
  if (prevBtn) prevBtn.addEventListener('click', prev);
  dots.forEach(function (dot, i) {
    dot.addEventListener('click', function () {
      // The active dot is the pause button; the rest are still jump links.
      // It replaced a press-and-hold gesture on the photo, which only ever
      // served a mouse and had to advertise itself with a label to be found
      // at all.
      if (toggleEl && i === index) {
        if (pauseReasons.toggle) removePause('toggle');
        else addPause('toggle');
        return;
      }
      goTo(i);
    });
  });

  // Focus inside a SLIDE pauses, so a keyboard user reading a CTA does not
  // have it swapped out from under them. Focus inside the CONTROLS must not:
  // the pause button lives there, and a blanket focus pause would re-pause
  // the carousel the instant the button was used to resume it.
  //
  // This is a convenience, not the accessibility story — WCAG 2.2.2 is met by
  // the pause button itself, which is a real focusable control rather than
  // the pointer-only press-and-hold gesture this replaced.
  function inControls(el) {
    return !!(el && el.closest && el.closest('.hero-carousel__controls'));
  }
  root.addEventListener('focusin', function (e) {
    if (inControls(e.target)) return;
    addPause('focus');
  });
  root.addEventListener('focusout', function (e) {
    var to = e.relatedTarget;
    if (root.contains(to) && !inControls(to)) return;
    removePause('focus');
  });
  document.addEventListener('visibilitychange', function () {
    document.hidden ? addPause('hidden') : removePause('hidden');
  });

  root.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
  });

  var touchStartX = null;
  root.addEventListener('touchstart', function (e) { touchStartX = e.touches[0].clientX; }, { passive: true });
  root.addEventListener('touchend', function (e) {
    if (touchStartX === null) return;
    var delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 50) { delta < 0 ? next() : prev(); }
    touchStartX = null;
  }, { passive: true });

  goTo(0);
}

/* ── Product page: variants, quantity, add-to-bag bar ──────────
   The mobile bar's "add to cart" CLICKS the real add-to-cart button rather
   than posting on its own — see the bar's click handler below.
*/
function initProductPage() {
  var form = document.querySelector('[data-product-form]');
  if (!form) return;

  // Set up before the variant logic's early return: the gallery has to work
  // on every product page, including ones with nothing to buy (auctions).
  var gallery = initProductGallery();

  var variantScript = document.querySelector('[data-variants]');
  var variants = [];
  try {
    variants = variantScript ? JSON.parse(variantScript.textContent) : [];
  } catch (e) {
    variants = [];
  }

  var variantIdInput = form.querySelector('[data-variant-id]');
  var atcBtn = form.querySelector('[data-atc-btn]');
  var priceRow = document.querySelector('[data-price-row]');
  var priceEl = document.querySelector('[data-product-price]');
  var comparePriceEl = document.querySelector('[data-compare-price]');
  var comparePriceValueEl = document.querySelector('[data-compare-price-value]');
  var addonsNote = document.querySelector('[data-price-addons]');
  var skuEl = document.querySelector('[data-product-sku]');
  var stickyPriceEl = document.querySelector('[data-sticky-price]');
  var stickyAtcBtn = document.querySelector('[data-sticky-atc]');
  var stickyImage = document.querySelector('[data-sticky-image]');
  var qtyInput = form.querySelector('[data-qty-input]');
  var qtyDec = form.querySelector('[data-qty-dec]');
  var qtyInc = form.querySelector('[data-qty-inc]');
  var radios = Array.prototype.slice.call(form.querySelectorAll('[data-opt-radio]'));
  var selects = Array.prototype.slice.call(form.querySelectorAll('[data-variant-select]'));

  if (!variantIdInput || !variants.length) return;

  var didFirstUpdate = false;
  var selected = [];

  /* What the product-options app is currently charging on top of the
     variant, in cents. Kept here rather than read on demand because the
     price has to be re-rendered on a variant change too, and by then the
     app may not have re-announced it. See OPTION_PRICE_EVENT. */
  var addonCents = 0;
  var currentVariant = null;
  var moneyFormat = priceRow ? priceRow.getAttribute('data-money-format') : '';

  /* Baseline = the options of the variant the server rendered. Options that
     produce no control still need a value: the markup deliberately omits the
     picker for a lone "Default Title" option, so an optionless product had
     NOTHING in `selected`, findVariant's exact match never hit, and every
     such product rendered a disabled "sold out" button — including the ones
     that are always in stock because Shopify doesn't track them. Any option
     that does render a control overwrites its slot below. */
  var initialVariant = null;
  for (var iv = 0; iv < variants.length; iv++) {
    if (String(variants[iv].id) === String(variantIdInput.value)) {
      initialVariant = variants[iv];
      break;
    }
  }
  var baseline = initialVariant ? initialVariant.options.slice() : [];
  currentVariant = initialVariant;

  function readSelection() {
    selected = baseline.slice();
    radios.forEach(function (r) {
      if (r.checked) selected[parseInt(r.dataset.optionIndex, 10)] = r.value;
    });
    selects.forEach(function (s) {
      selected[parseInt(s.dataset.optionIndex, 10)] = s.value;
    });
  }

  // Exact match on every option. The previous version treated an
  // unset option as a wildcard, so it could resolve to an unrelated variant.
  function findVariant(combo) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var hit = true;
      for (var j = 0; j < v.options.length; j++) {
        if (combo[j] !== v.options[j]) { hit = false; break; }
      }
      if (hit) return v;
    }
    return null;
  }

  /* Grey out option values that can't be bought with the rest of the current
     selection, instead of letting shoppers pick a dead combination and only
     discover it from a disabled button. */
  function markUnavailableOptions() {
    radios.forEach(function (radio) {
      var idx = parseInt(radio.dataset.optionIndex, 10);
      var probe = selected.slice();
      probe[idx] = radio.value;
      var match = findVariant(probe);
      var label = radio.closest('.product__opt-btn');
      if (!label) return;
      var dead = !match || !match.available;
      label.classList.toggle('is-unavailable', dead);
      if (dead) label.setAttribute('data-unavailable', 'true');
      else label.removeAttribute('data-unavailable');
    });
  }

  function setButtonState(btn, variant) {
    if (!btn) return;
    btn.disabled = !variant || !variant.available;
    var text;
    if (!variant) text = btn.dataset.unavailableText || btn.dataset.soldOutText;
    else if (!variant.available) text = btn.dataset.soldOutText;
    else text = btn.dataset.addText;
    if (!text) return;
    var label = btn.querySelector('[data-btn-label]');
    (label || btn).textContent = text;
  }

  function syncUrl(variant) {
    if (!variant || !window.history || !history.replaceState) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    // replaceState, not pushState: variant switching shouldn't bury the
    // previous page under a stack of history entries.
    history.replaceState({ variantId: variant.id }, '', url.toString());
  }

  /* Price = variant price + whatever the options app charges for the
     current selection. Folding the surcharge in is not cosmetic: the app
     bills it on the SAME cart line (a €79,99 jersey with the €10 patch
     comes back from /cart.js as one line at 8999, tagged with the property
     `_fpob_option_price`), so a PDP that keeps showing €79,99 understates
     what the shopper is about to pay.

     With no surcharge selected the string is Liquid's own `price_formatted`
     verbatim, so the overwhelmingly common case never goes near the JS
     money formatter.

     The compare-at price is deliberately NOT adjusted. It is a claim about
     a price this product was previously sold at, which under German PAngV
     has to be a price that actually existed; "€79,99 was €99,99" plus a
     €10 patch is two true statements, whereas a synthesised "was €109,99"
     is a reference price nobody was ever charged. The add-on note under
     the price is what explains the difference. */
  function renderPrice() {
    var variant = currentVariant;
    if (!variant) return;

    var onSale = variant.compare_at_price > variant.price;
    var formatted = addonCents
      ? formatMoney(variant.price + addonCents, moneyFormat)
      : variant.price_formatted;

    if (priceEl) {
      priceEl.textContent = formatted;
      priceEl.classList.toggle('is-sale', onSale);
    }
    if (comparePriceEl) {
      comparePriceEl.hidden = !onSale;
      // Write into the inner value span, not the wrapper: the wrapper also
      // holds a visually-hidden "regular price" label that setting
      // textContent on it would delete.
      if (onSale && comparePriceValueEl) comparePriceValueEl.textContent = variant.compare_at_price_formatted;
    }
    if (stickyPriceEl) stickyPriceEl.textContent = formatted;

    if (addonsNote) {
      addonsNote.hidden = !addonCents;
      if (addonCents) {
        // data-addons-template carries the translated string with a [price]
        // placeholder, same pattern as the SKU line above.
        addonsNote.textContent = (addonsNote.getAttribute('data-addons-template') || '')
          .replace('[price]', formatMoney(addonCents, moneyFormat));
      }
    }
  }

  document.addEventListener(OPTION_PRICE_EVENT, function (e) {
    var cents = (e.detail && e.detail.cents) || 0;
    if (cents === addonCents) return;
    addonCents = cents;
    renderPrice();
  });

  function updateVariant() {
    readSelection();
    markUnavailableOptions();

    var variant = findVariant(selected);
    setButtonState(atcBtn, variant);
    setButtonState(stickyAtcBtn, variant);

    if (!variant) return;

    variantIdInput.value = variant.id;

    currentVariant = variant;
    renderPrice();

    if (skuEl) {
      skuEl.hidden = !variant.sku;
      // data-sku-template carries the translated string with a [sku]
      // placeholder, so the label stays localised instead of being
      // reconstructed by string-surgery on whatever is already rendered.
      if (variant.sku && skuEl.dataset.skuTemplate) {
        skuEl.textContent = skuEl.dataset.skuTemplate.replace('[sku]', variant.sku);
      }
    }

    if (qtyInput) {
      if (variant.inventory_left !== null && variant.inventory_left > 0) {
        qtyInput.max = variant.inventory_left;
        if (parseInt(qtyInput.value, 10) > variant.inventory_left) qtyInput.value = variant.inventory_left;
      } else {
        qtyInput.removeAttribute('max');
      }
    }

    // On first paint the server already rendered the right main image,
    // active thumb and bar thumbnail, so only a real variant change moves
    // them. The thumbnail strip is filtered either way.
    if (gallery) gallery.showVariant(variant.id, didFirstUpdate);
    if (stickyImage && didFirstUpdate) {
      stickyImage.src = variant.thumb || stickyImage.getAttribute('data-default-src');
      // Cropped around the same focus point the merchant set on that image.
      stickyImage.style.objectPosition = (variant.thumb && variant.thumb_focal) ||
        stickyImage.getAttribute('data-default-focal') || '';
    }
    didFirstUpdate = true;
    syncUrl(variant);
  }

  radios.forEach(function (radio) {
    radio.addEventListener('change', updateVariant);
  });
  selects.forEach(function (select) {
    select.addEventListener('change', updateVariant);
  });

  if (qtyInput && qtyDec && qtyInc) {
    qtyDec.addEventListener('click', function () {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
    qtyInc.addEventListener('click', function () {
      var max = parseInt(qtyInput.max, 10);
      var next = (parseInt(qtyInput.value, 10) || 1) + 1;
      qtyInput.value = isNaN(max) ? next : Math.min(max, next);
    });
    qtyInput.addEventListener('change', function () {
      var v = parseInt(qtyInput.value, 10);
      var max = parseInt(qtyInput.max, 10);
      if (isNaN(v) || v < 1) v = 1;
      if (!isNaN(max) && v > max) v = max;
      qtyInput.value = v;
    });
  }

  /* The bar's button goes through the REAL add-to-cart button. It used to
     call form.requestSubmit(), which fires no click — and the options app
     checks its required fields in a click handler on that button, so an
     unconfigured jersey went straight into the cart from the bar while the
     main button correctly refused it. .click() runs every listener the main
     button has, the app's included, and then submits exactly as a tap on
     the main button would.

     If nothing was submitted, something stopped it (the app, a required
     field) and says why next to the fields — which the shopper can't see:
     the bar only shows once those have scrolled away. So bring them back.
     The wait allows for an app that validates asynchronously before
     letting the click through; a submit, or the button going busy, inside
     that window means the add is under way. */
  var submitCount = 0;

  function revealBuyArea() {
    var target = null;
    var flagged = form.querySelectorAll('[class*="error"]');
    for (var i = 0; i < flagged.length && !target; i++) {
      if (flagged[i].offsetParent !== null && flagged[i].textContent.trim()) target = flagged[i];
    }
    target = target ||
      form.querySelector('.fpob-main-container') ||
      form.querySelector('.product-options') ||
      form;
    // No `behavior`: .windowInner's own scroll-behavior decides, and §25
    // already turns it off for reduced motion.
    target.scrollIntoView({ block: 'center' });
  }

  if (stickyAtcBtn) {
    stickyAtcBtn.addEventListener('click', function () {
      // An add already under way: a second tap is not a blocked one.
      if (atcBtn && atcBtn.classList.contains('is-loading')) return;
      if (!atcBtn || atcBtn.disabled) {
        revealBuyArea();
        return;
      }
      var before = submitCount;
      atcBtn.click();
      setTimeout(function () {
        if (submitCount === before && !atcBtn.classList.contains('is-loading')) revealBuyArea();
      }, 250);
    });
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    submitCount++;
    addToCart(form, atcBtn);
  });

  updateVariant();
}

/* ── Mobile add-to-bag bar ─────────────────────────────────────
   Shown only once the REAL add-to-cart button has scrolled up out of view,
   under the sticky header — never while it is still below the fold.

   It used to be "whenever the buy-buttons block isn't on screen", which
   read as random: on a phone that block starts below the fold, so the bar
   was up at page load, vanished as the shopper reached the button, and
   came back once they passed it. The block also contains the options app's
   whole UI (the app injects it there), so "on screen" spanned a screen and
   a half of fields, and the sticky nav covered the button while it still
   counted as visible.

   A scroll listener rather than an IntersectionObserver: the line that
   matters is the header's bottom edge, which differs between breakpoints
   (--nav-h), and reading it on each rAF-coalesced pass is simpler than
   rebuilding an observer's rootMargin on resize. #windowInner is the
   element that scrolls, not the window (see theme.css §6). */
function initStickyBuyBar() {
  var bar = document.querySelector('[data-product-mobile-bar]');
  var button = document.querySelector('[data-atc-btn]');
  var scroller = document.getElementById('windowInner');
  if (!bar || !button || !scroller) return;

  var header = document.getElementById('shopify-section-header');
  var queued = false;

  function sync() {
    queued = false;
    var line = header ? header.getBoundingClientRect().bottom : scroller.getBoundingClientRect().top;
    var rect = button.getBoundingClientRect();
    // A 0-high rect means the button isn't rendered at all (something hid
    // it); a shortcut to a button that isn't there would be worse than none.
    bar.classList.toggle('is-visible', rect.height > 0 && rect.bottom <= line);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(sync);
  }

  scroller.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  sync();
}

/* ── Product gallery: arrows, swipe, hover zoom ─────────────────
   The main image is ONE <img> whose source is swapped, not a track of
   slides: the variant filter (see the thumbnail comment in
   main-product.liquid) decides which images are in play, and one element
   keeps that a single rule instead of mirroring it onto a second set.

   "Items" are the thumbnails that aren't hidden, in DOM order. Arrows,
   swipe, the lightbox and variant switching all walk that list, so none of
   them can surface an image that belongs to a variant the shopper didn't
   pick. A product with a single image has no thumbnails; the image the
   server rendered is then the only item.

   Returns the small API initProductPage and the lightbox drive it with,
   or null on a product without images. */
function initProductGallery() {
  var main = document.querySelector('[data-gallery-main]');
  var mainImage = main && main.querySelector('[data-main-image]');
  if (!mainImage) return null;

  var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-thumb]'));
  var prevBtn = main.querySelector('[data-gallery-prev]');
  var nextBtn = main.querySelector('[data-gallery-next]');
  var hoverImg = main.querySelector('[data-hover-zoom]');

  var single = {
    src: mainImage.getAttribute('src'),
    srcset: mainImage.getAttribute('srcset') || '',
    zoom: mainImage.getAttribute('data-zoom-src'),
    zoomWidth: parseInt(mainImage.getAttribute('data-zoom-width'), 10) || 0,
    alt: mainImage.getAttribute('alt') || '',
    focal: mainImage.style.objectPosition || '',
  };

  function data(thumb) {
    if (!thumb) return single;
    return {
      src: thumb.dataset.mainSrc,
      srcset: thumb.dataset.mainSrcset || '',
      zoom: thumb.dataset.zoomSrc,
      zoomWidth: parseInt(thumb.dataset.zoomWidth, 10) || 0,
      alt: thumb.dataset.alt || '',
      // The merchant's focus point: object-fit: cover crops around it.
      focal: thumb.dataset.focal || '',
    };
  }

  var current = thumbs.filter(function (t) { return t.classList.contains('is-active'); })[0] || null;

  /* The general shot to fall back on. Deliberately the first non-variant
     thumb rather than whatever `src` the page opened with: on a ?variant=
     deep link the page opens on that variant's own image, so reusing it as
     the fallback left the main image on one player's jersey while the
     active thumb moved to the general one. */
  var defaultThumb = thumbs.filter(function (t) { return !t.dataset.variantIds; })[0] || null;

  function items() {
    return thumbs.filter(function (t) { return !t.hidden; });
  }

  function setActiveThumb(active) {
    thumbs.forEach(function (t) {
      t.classList.toggle('is-active', t === active);
      t.setAttribute('aria-current', t === active ? 'true' : 'false');
    });
    revealThumb(active);
  }

  /* On phones the thumbnails are one sideways-scrolling row (theme.css §14),
     so swiping the big image to a thumb that's scrolled out of sight has to
     bring it back into view. The strip's own scrollLeft only — never
     scrollIntoView, which would also scroll #windowInner to the strip and
     yank the page while the shopper is looking at the image. A no-op on
     desktop, where the strip wraps and never overflows. */
  var strip = thumbs.length ? thumbs[0].parentElement : null;
  function revealThumb(thumb) {
    if (!strip || !thumb || thumb.hidden || strip.scrollWidth <= strip.clientWidth) return;
    var pad = parseFloat(getComputedStyle(strip).scrollPaddingLeft) || 0;
    var box = strip.getBoundingClientRect();
    var r = thumb.getBoundingClientRect();
    var target = null;
    if (r.left < box.left + pad) target = strip.scrollLeft - (box.left + pad - r.left);
    else if (r.right > box.right - pad) target = strip.scrollLeft + (r.right - (box.right - pad));
    if (target === null) return;
    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (strip.scrollTo) strip.scrollTo({ left: target, behavior: behavior });
    else strip.scrollLeft = target;
  }

  function syncNav() {
    var many = items().length > 1;
    if (prevBtn) prevBtn.hidden = !many;
    if (nextBtn) nextBtn.hidden = !many;
  }

  function show(thumb) {
    if (!thumb) return;
    var d = data(thumb);
    current = thumb;
    // srcset before src: once srcset is present the browser picks from it,
    // so setting src first would start a download it then throws away.
    if (d.srcset) mainImage.setAttribute('srcset', d.srcset);
    else mainImage.removeAttribute('srcset');
    mainImage.src = d.src;
    mainImage.alt = d.alt;
    // The hover magnifier is the same crop scaled up, so it moves with it.
    mainImage.style.objectPosition = d.focal;
    if (hoverImg) hoverImg.style.objectPosition = d.focal;
    mainImage.setAttribute('data-zoom-src', d.zoom);
    mainImage.setAttribute('data-zoom-width', d.zoomWidth);
    setActiveThumb(thumb);
    refreshHover();
  }

  function step(dir) {
    var list = items();
    if (list.length < 2) return;
    var i = Math.max(0, list.indexOf(current));
    show(list[(i + dir + list.length) % list.length]);
    slideIn(mainImage, dir);
  }

  /* Filters the strip down to general media + this variant's own image.
     `move` also switches the main image: to the variant's image, or — when
     it has none — back to the general shot, rather than leaving the
     previous variant's jersey on screen next to a different player's name. */
  function showVariant(id, move) {
    id = String(id);
    var own = null;
    thumbs.forEach(function (thumb) {
      var owners = thumb.dataset.variantIds ? thumb.dataset.variantIds.split(' ') : [];
      var mine = owners.indexOf(id) !== -1;
      thumb.hidden = owners.length > 0 && !mine;
      if (mine && !own) own = thumb;
    });
    syncNav();
    if (move) show(own || defaultThumb);
  }

  thumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () { show(thumb); });
    // Every thumb is warmed, variant-bound ones included. They start hidden,
    // but switching variant swaps the MAIN image to that variant's shot — so
    // waiting until the click meant the big image visibly loaded in after the
    // selection instead of appearing with it. Same srcset/sizes as the main
    // image, so the browser warms the size it will actually pick.
    var warm = new Image();
    warm.sizes = mainImage.getAttribute('sizes') || '';
    if (thumb.dataset.mainSrcset) warm.srcset = thumb.dataset.mainSrcset;
    warm.src = thumb.dataset.mainSrc;
  });

  if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

  /* ── Desktop hover zoom ──
     A second <img> over the main one, scaled up with its transform-origin
     under the cursor — the same object-fit crop as the image below, just
     magnified, so the picture doesn't jump when the zoom kicks in. It shows
     the image already on screen at once and sharpens to the original upload
     when that has downloaded; only someone who actually hovers fetches it.
     Mouse only: on touch there is no hover, and the tap opens the lightbox. */
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var hovering = false;
  var loadedZoom = {};

  function refreshHover() {
    if (!hovering || !hoverImg) return;
    var url = mainImage.getAttribute('data-zoom-src');
    if (loadedZoom[url]) {
      hoverImg.src = url;
      return;
    }
    hoverImg.src = mainImage.currentSrc || mainImage.src;
    preloadImage(url, function (ok) {
      if (!ok) return;
      loadedZoom[url] = true;
      if (hovering && mainImage.getAttribute('data-zoom-src') === url) hoverImg.src = url;
    });
  }

  function startHover() {
    if (hovering || !hoverImg) return;
    hovering = true;
    refreshHover();
    main.classList.add('is-zooming');
  }

  function stopHover() {
    hovering = false;
    main.classList.remove('is-zooming');
  }

  main.addEventListener('pointermove', function (e) {
    if (e.pointerType !== 'mouse' || !(finePointer && finePointer.matches)) return;
    // The arrows and the zoom button sit on top of the image; magnifying
    // under them would hide what they're for.
    if (e.target.closest('button')) {
      stopHover();
      return;
    }
    startHover();
    var r = main.getBoundingClientRect();
    var x = clamp((e.clientX - r.left) / r.width, 0, 1) * 100;
    var y = clamp((e.clientY - r.top) / r.height, 0, 1) * 100;
    hoverImg.style.transformOrigin = x + '% ' + y + '%';
  });
  main.addEventListener('pointerleave', stopHover);

  // ── Touch swipe between items ──
  var justSwiped = bindSwipe(main, {
    enabled: function () { return items().length > 1; },
    follower: function () { return mainImage; },
    onSwipe: step,
  });

  var api = {
    items: items,
    data: data,
    current: function () { return current; },
    pageSrc: function () { return mainImage.currentSrc || mainImage.src; },
    show: show,
    showVariant: showVariant,
  };

  // ── Lightbox ──
  var lightbox = initLightbox(api);
  function openLightbox() {
    stopHover();
    if (lightbox) lightbox.open();
  }
  main.addEventListener('click', function (e) {
    if (justSwiped(e) || e.target.closest('button')) return;
    openLightbox();
  });
  var zoomBtn = main.querySelector('[data-lightbox-open]');
  if (zoomBtn) zoomBtn.addEventListener('click', openLightbox);

  syncNav();
  return api;
}

/* The incoming image drifts in from the side the swipe came from. Web
   Animations rather than a CSS class, so nothing has to be cleaned up
   afterwards; skipped entirely under reduced motion (§25's blanket rule
   only reaches CSS animations, not these). Transforms here act on the
   image inside .windowInner, never on the scroll shell — README. */
function slideIn(el, dir) {
  if (prefersReducedMotion() || typeof el.animate !== 'function') return;
  el.animate(
    [{ transform: 'translateX(' + dir * 48 + 'px)', opacity: 0 }, { transform: 'none', opacity: 1 }],
    { duration: 220, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
  );
}

/* Horizontal swipe for touch and pen, shared by the page gallery and the
   lightbox. The element's CSS touch-action must leave horizontal movement
   to us (pan-y, or none); the browser keeps vertical scrolling and cancels
   the gesture if that is what the finger does. What follows the finger is
   `follower()`; past 50px, or a quick flick, counts as a swipe.

   Returns justSwiped(event): true for the click a browser may still fire
   at the end of a swipe, which must not count as a tap. A time window
   rather than a flag, because whether that click fires at all differs
   between browsers — a flag nothing consumed would eat the next real tap. */
function bindSwipe(el, opts) {
  var drag = null;
  var quietUntil = 0;

  el.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse' || e.target.closest('button') || !opts.enabled()) return;
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp, dx: 0, axis: null };
  });

  el.addEventListener('pointermove', function (e) {
    if (!drag || e.pointerId !== drag.id) return;
    var dx = e.clientX - drag.x;
    var dy = e.clientY - drag.y;
    if (!drag.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (drag.axis !== 'x') return;
    drag.dx = dx;
    opts.follower().style.transform = 'translateX(' + dx + 'px)';
  });

  function end(e) {
    if (!drag || e.pointerId !== drag.id) return;
    var d = drag;
    drag = null;
    if (d.axis !== 'x') return;

    quietUntil = e.timeStamp + 250;
    var follower = opts.follower();
    follower.style.transform = '';
    var elapsed = Math.max(1, e.timeStamp - d.t);
    var flick = Math.abs(d.dx) > 20 && Math.abs(d.dx) / elapsed > 0.5;
    if (e.type === 'pointerup' && (Math.abs(d.dx) > 50 || flick)) {
      // Finger moving left brings in the NEXT image.
      opts.onSwipe(d.dx < 0 ? 1 : -1);
    } else if (!prefersReducedMotion() && typeof follower.animate === 'function') {
      follower.animate(
        [{ transform: 'translateX(' + d.dx + 'px)' }, { transform: 'none' }],
        { duration: 180, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
    }
  }
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);

  return function justSwiped(e) {
    return e.timeStamp < quietUntil;
  };
}

/* ── Lightbox ───────────────────────────────────────────────────
   Fullscreen viewer over the gallery's current items, built on
   createOverlay() like every other overlay (Escape, focus trap, focus
   return, `inert` while closed, scroll lock on #windowInner).

   It opens instantly on the image already downloaded for the page and
   swaps to the original upload once that has arrived — the originals are
   2000–4000px, too big to make anyone wait for.

   Zoom is a toggle (tap/click, or the zoom button) rather than pinch: the
   zoomed image is laid out at its native resolution — clamped to 2–3× the
   fitted size — inside a scrollable stage, so panning is plain scrolling
   on touch, and follows the mouse on desktop. */
function initLightbox(gallery) {
  var panel = document.querySelector('[data-lightbox]');
  if (!panel) return null;

  var stage = panel.querySelector('[data-lightbox-stage]');
  var img = panel.querySelector('[data-lightbox-img]');
  var counter = panel.querySelector('[data-lightbox-counter]');
  var zoomBtn = panel.querySelector('[data-lightbox-zoom]');
  var closeBtn = panel.querySelector('[data-lightbox-close]');
  var prevBtn = panel.querySelector('[data-lightbox-prev]');
  var nextBtn = panel.querySelector('[data-lightbox-next]');

  var list = [];
  var index = 0;
  var zoomed = false;
  var renderToken = 0;
  var lastPointer = 'mouse';

  var overlay = createOverlay({
    panel: panel,
    initialFocus: '[data-lightbox-close]',
    onClose: function () {
      setZoom(false);
      // Leave the page on the image the shopper was last looking at.
      gallery.show(list[index]);
    },
  });

  function open() {
    var visible = gallery.items();
    list = visible.length ? visible : [null];
    index = Math.max(0, list.indexOf(gallery.current()));
    render(true);
    overlay.open();
  }

  function render(fromPage) {
    var d = gallery.data(list[index]);
    var token = ++renderToken;
    setZoom(false);
    img.alt = d.alt;
    // The image on the page is already in cache — show it this instant.
    img.src = fromPage ? gallery.pageSrc() : d.src;
    panel.classList.add('is-loading');
    preloadImage(d.zoom, function (ok) {
      if (token !== renderToken) return;
      panel.classList.remove('is-loading');
      if (ok) img.src = d.zoom;
      // Then the neighbours, so stepping through doesn't wait either.
      [-1, 1].forEach(function (dir) {
        var n = list[(index + dir + list.length) % list.length];
        if (n && list.length > 1) preloadImage(gallery.data(n).zoom);
      });
    });

    var many = list.length > 1;
    if (prevBtn) prevBtn.hidden = !many;
    if (nextBtn) nextBtn.hidden = !many;
    if (counter) {
      counter.hidden = !many;
      counter.textContent = (counter.getAttribute('data-template') || '')
        .replace('[current]', index + 1)
        .replace('[total]', list.length);
    }
  }

  function go(dir) {
    if (list.length < 2) return;
    index = (index + dir + list.length) % list.length;
    render(false);
    slideIn(img, dir);
  }

  /* Where the picture is actually painted: the <img> box fills the stage
     and object-fit: contain letterboxes the image inside it. */
  function paintedRect() {
    var box = img.getBoundingClientRect();
    var ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    var w = box.width;
    var h = box.height;
    if (w / h > ratio) w = h * ratio;
    else h = w / ratio;
    var left = box.left + (box.width - w) / 2;
    var top = box.top + (box.height - h) / 2;
    return { left: left, top: top, right: left + w, bottom: top + h, width: w, height: h, ratio: ratio };
  }

  function setZoom(on, x, y) {
    if (on === zoomed) return;
    if (!on) {
      zoomed = false;
      panel.classList.remove('is-zoomed');
      img.style.width = '';
      img.style.height = '';
      stage.scrollLeft = 0;
      stage.scrollTop = 0;
    } else {
      var r = paintedRect();
      var s = stage.getBoundingClientRect();
      var px = x == null ? s.left + s.width / 2 : x;
      var py = y == null ? s.top + s.height / 2 : y;
      // Which point of the picture was pointed at, as a fraction of it.
      var fx = clamp((px - r.left) / r.width, 0, 1);
      var fy = clamp((py - r.top) / r.height, 0, 1);

      var d = gallery.data(list[index]);
      var native = (d.zoomWidth || img.naturalWidth) / (window.devicePixelRatio || 1);
      var width = clamp(native, r.width * 2, r.width * 3);

      zoomed = true;
      panel.classList.add('is-zoomed');
      img.style.width = width + 'px';
      img.style.height = width / r.ratio + 'px';

      if (lastPointer === 'mouse') {
        panTo(px, py);
      } else {
        // Keep the tapped point under the finger.
        stage.scrollLeft = img.offsetLeft + fx * img.offsetWidth - (px - s.left);
        stage.scrollTop = img.offsetTop + fy * img.offsetHeight - (py - s.top);
      }
    }
    if (zoomBtn) zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
  }

  // Desktop panning: the pointer's place in the stage maps to the same
  // place in the picture, so the whole image is reachable without dragging.
  function panTo(x, y) {
    var s = stage.getBoundingClientRect();
    stage.scrollLeft = clamp((x - s.left) / s.width, 0, 1) * (stage.scrollWidth - stage.clientWidth);
    stage.scrollTop = clamp((y - s.top) / s.height, 0, 1) * (stage.scrollHeight - stage.clientHeight);
  }

  stage.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || 'mouse'; });
  stage.addEventListener('pointermove', function (e) {
    if (zoomed && e.pointerType === 'mouse') panTo(e.clientX, e.clientY);
  });

  var justSwiped = bindSwipe(stage, {
    enabled: function () { return !zoomed && list.length > 1; },
    follower: function () { return img; },
    onSwipe: go,
  });

  stage.addEventListener('click', function (e) {
    if (justSwiped(e)) return;
    if (zoomed) {
      setZoom(false);
      return;
    }
    // On the picture a click zooms in at that point. Beside it, a mouse
    // click closes, as in most viewers — but not a tap: on a phone the
    // bands above and below a square photo are half the screen, and a
    // stray thumb there shouldn't throw the shopper out.
    var r = paintedRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
      if (lastPointer === 'mouse') overlay.close();
      return;
    }
    setZoom(true, e.clientX, e.clientY);
  });

  if (zoomBtn) {
    zoomBtn.addEventListener('click', function () {
      lastPointer = 'keyboard';
      setZoom(!zoomed);
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', overlay.close);
  if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { go(1); });

  /* Capture phase on document, so it runs before createOverlay's own
     Escape handler (a bubble listener on document) wherever focus happens
     to be: with the image zoomed, Escape zooms out first instead of
     throwing the shopper out of the viewer. */
  document.addEventListener('keydown', function (e) {
    if (!overlay.isOpen()) return;
    if (e.key === 'Escape' && zoomed) {
      e.preventDefault();
      e.stopPropagation();
      setZoom(false);
    } else if (!zoomed && e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    } else if (!zoomed && e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  }, true);

  // The zoomed size was computed for the old viewport width. Height-only
  // resizes (a phone's toolbar sliding in and out) don't invalidate it and
  // would otherwise zoom the shopper out mid-pan.
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    if (zoomed) setZoom(false);
  });

  return { open: open };
}

/* ── AJAX cart ─────────────────────────────────────────────────
   The drawer is re-fetched wholesale via the Section Rendering API after
   every mutation, so money formatting/locale stay server-side rather than
   duplicated in JS.
*/
var cartDrawerOverlay = null;

function initCartDrawer() {
  var drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  cartDrawerOverlay = createOverlay({
    panel: drawer,
    overlayEl: document.querySelector('[data-cart-drawer-overlay]'),
    initialFocus: '[data-cart-drawer-close]',
  });

  // Delegated: the drawer's contents are replaced wholesale on every refresh,
  // so listeners bound to inner nodes would not survive.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer-close]')) {
      e.preventDefault();
      closeCartDrawer();
    }
  });

  // Two of them: the desktop header's and the phone bar's (.nav-quick).
  document.querySelectorAll('[data-cart-icon]').forEach(function (cartIcon) {
    cartIcon.addEventListener('click', function (e) {
      e.preventDefault();
      openCartDrawer();
    });
  });

  bindCartControls(drawer);
}

function openCartDrawer() { if (cartDrawerOverlay) cartDrawerOverlay.open(); }
function closeCartDrawer() { if (cartDrawerOverlay) cartDrawerOverlay.close(); }

/* Per-line debounce timers. A single shared timer meant adjusting two
   different lines in quick succession silently dropped the first update. */
var cartLineTimers = {};

function bindCartControls(scope) {
  if (!scope) return;

  scope.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var line = btn.closest('[data-cart-line]');
      if (!line) return;
      setLineBusy(line, true);
      changeCartLine(line.dataset.cartLine, 0);
    });
  });

  scope.querySelectorAll('[data-cart-qty-dec], [data-cart-qty-inc]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var line = btn.closest('[data-cart-line]');
      if (!line) return;
      var input = line.querySelector('[data-cart-qty-input]');
      var qty = parseInt(input.value, 10) || 1;
      qty = btn.hasAttribute('data-cart-qty-dec') ? Math.max(1, qty - 1) : qty + 1;
      input.value = qty;
      setLineBusy(line, true);
      changeCartLine(line.dataset.cartLine, qty);
    });
  });

  scope.querySelectorAll('[data-cart-qty-input]').forEach(function (input) {
    input.addEventListener('change', function () {
      var line = input.closest('[data-cart-line]');
      if (!line) return;
      var key = line.dataset.cartLine;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 1) { input.value = 1; qty = 1; }
      clearTimeout(cartLineTimers[key]);
      cartLineTimers[key] = setTimeout(function () {
        setLineBusy(line, true);
        changeCartLine(key, qty);
      }, 400);
    });
  });
}

function setLineBusy(line, busy) {
  line.classList.toggle('is-busy', !!busy);
  line.setAttribute('aria-busy', busy ? 'true' : 'false');
}

function setButtonLoading(btn, loading) {
  if (!btn) return;
  btn.classList.toggle('is-loading', !!loading);
  if (loading) btn.setAttribute('aria-busy', 'true');
  else btn.removeAttribute('aria-busy');
  btn.disabled = !!loading;
}

function showAtcStatus(message, isError) {
  var el = document.querySelector('[data-atc-status]');
  if (!el) {
    if (isError) window.alert(message);
    return;
  }
  el.textContent = message;
  el.hidden = false;
  el.classList.toggle('is-error', !!isError);
}

function clearAtcStatus() {
  var el = document.querySelector('[data-atc-status]');
  if (el) { el.hidden = true; el.textContent = ''; el.classList.remove('is-error'); }
}

function addToCart(form, btn) {
  clearAtcStatus();
  setButtonLoading(btn, true);

  fetch(rootUrl() + 'cart/add.js', {
    method: 'POST',
    body: new FormData(form),
    headers: { Accept: 'application/json' },
  })
    .then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw body;
        return body;
      });
    })
    .then(function () { return refreshCart(); })
    .then(function () {
      setButtonLoading(btn, false);
      openCartDrawer();
    })
    .catch(function (err) {
      setButtonLoading(btn, false);
      // Shopify returns a human-readable `description` for cart errors
      // (e.g. "You can only add 3 of this item"); surface it inline rather
      // than in a browser alert() the shopper has to dismiss.
      var msg = (err && (err.description || err.message)) ||
        (form.dataset.errorText || 'Dieses Produkt konnte nicht zum Warenkorb hinzugefügt werden.');
      showAtcStatus(msg, true);
    });
}

function changeCartLine(key, quantity) {
  return fetch(rootUrl() + 'cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity: quantity }),
  })
    .then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw body;
        return body;
      });
    })
    .then(function () { return refreshCart(); })
    .catch(function () { window.location.reload(); });
}

/* Refreshes the drawer, the header count and — when we're on /cart — the
   cart page itself, all from one render pass. The cart page previously did a
   full window.location.reload() after every quantity tap, which threw away
   scroll position and flashed the whole page for a one-line change. */
function refreshCart() {
  var cartRoot = document.querySelector('[data-cart-root]');
  var jobs = [fetch(sectionUrl('cart-drawer')).then(function (r) { return r.text(); })];

  if (cartRoot) {
    jobs.push(
      fetch(sectionUrl(cartRoot.dataset.cartRoot || 'main-cart', window.location.pathname))
        // A wrong section id answers 404 with an empty body, which still
        // RESOLVES the fetch — so the old code swallowed it and left the row
        // greyed out forever. Throwing here routes it to changeCartLine's
        // catch, which falls back to a reload: degraded, but never stuck.
        .then(function (r) {
          if (!r.ok) throw new Error('cart section ' + r.status);
          return r.text();
        })
    );
  }

  return Promise.all(jobs)
    .then(function (results) {
      var parser = new DOMParser();

      var freshDrawer = parser.parseFromString(results[0], 'text/html').querySelector('[data-cart-drawer]');
      var currentDrawer = document.getElementById('cart-drawer');
      if (freshDrawer && currentDrawer) {
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        bindCartControls(currentDrawer);
      }

      if (cartRoot && results[1]) {
        var freshCart = parser.parseFromString(results[1], 'text/html').querySelector('[data-cart-root]');
        if (!freshCart) throw new Error('cart section markup missing [data-cart-root]');
        cartRoot.innerHTML = freshCart.innerHTML;
        // bindCartControls covers the freshly-inserted nodes; calling
        // initCartPage() here as well would bind every listener twice.
        bindCartControls(cartRoot);
      }

      return fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
    })
    .then(function (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = '(' + cart.item_count + ')';
        el.hidden = cart.item_count === 0;
      });
      return cart;
    });
}

/* ── Full cart page ────────────────────────────────────────────*/
function initCartPage() {
  var page = document.querySelector('[data-cart-page-items]');
  if (!page) return;
  bindCartControls(page);
}

/* ── Account: address book add/edit toggles ────────────────────*/
function initAddressForms() {
  /* Uses the `hidden` attribute rather than reading back an inline
     style.display that was never set in the first place — the old check
     ("is it currently 'none'?") was false on a panel hidden by CSS, so the
     first click set display:none on an already-hidden panel and appeared to
     do nothing at all. */
  function toggle(panel, btn) {
    if (!panel) return;
    var nowHidden = !panel.hidden;
    panel.hidden = nowHidden;
    if (btn) btn.setAttribute('aria-expanded', nowHidden ? 'false' : 'true');
    if (!nowHidden) {
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }
  }

  var newPanel = document.querySelector('[data-address-form-new]');
  document.querySelectorAll('[data-address-new-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () { toggle(newPanel, btn); });
  });

  document.querySelectorAll('[data-address-edit-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      toggle(document.querySelector('[data-address-form-edit="' + btn.dataset.addressEditToggle + '"]'), btn);
    });
  });

  document.querySelectorAll('[data-address-cancel]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var panel = btn.closest('[data-address-form-new], [data-address-form-edit]');
      if (panel) panel.hidden = true;
    });
  });
}

/* ── Collection filters ────────────────────────────────────────
   The filter form is a plain GET form and the categories are plain links,
   so the page works with zero JS. This upgrades the form to live filtering:
   every change re-renders the section's [data-filter-region] blocks in place
   (counts, filter values, the sheet's "show N results" button, the results)
   from the Section Rendering API, and pushes the new URL so it stays
   shareable and Back steps through the choices.

   It used to submit the form on every change. On a phone that meant a page
   load per tap, and the load closed the filter drawer after every single
   choice. Now the sheet stays open until the shopper is done.

   Anything unexpected (a failed fetch, a response without the regions) falls
   back to loading the URL normally: slower, but never stuck.
*/
function initCollectionFilters() {
  var form = document.getElementById('CollectionFilterForm');
  var shell = document.querySelector('[data-collection-shell]');
  if (!form || !shell) return;

  var sectionId = shell.dataset.sectionId;
  var sidebar = document.querySelector('[data-filters-sidebar]');
  var openBtn = document.querySelector('[data-filters-open]');
  var sortSelect = document.querySelector('[data-sort-select]');
  var sortLabel = document.querySelector('[data-sort-label]');
  var mq = window.matchMedia('(max-width: 900px)');
  var overlay = null;

  var timer = null;
  var controller = null;
  var requestId = 0;
  var lastSearch = window.location.search.replace(/^\?/, '');
  // Set when results change behind the open sheet; they are revealed once
  // it closes (the scroll lock restores the old position on close).
  var revealOnClose = false;

  /* The query a plain GET submit would send, minus empty fields (the price
     boxes are usually blank), minus the collection's default sort (it is
     what no sort_by means anyway) and minus `page`: a new filter starts on
     page 1. FormData(form) includes the sort select, which sits outside the
     form and joins it through its form="" attribute. */
  var defaultSort = sortSelect ? sortSelect.dataset.defaultSort : '';
  function buildSearch() {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value === '' || (key === 'sort_by' && value === defaultSort)) return;
      params.append(key, value);
    });
    return params.toString();
  }

  function schedule() {
    clearTimeout(timer);
    // Coalesces a quick run of taps into one request.
    timer = setTimeout(function () { render(buildSearch(), true); }, 150);
  }

  function setBusy(busy) {
    shell.classList.toggle('is-loading', busy);
    var results = shell.querySelector('[data-filter-region="results"]');
    if (results) results.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* Swaps every region for its fresh copy. If focus was inside something
     that got replaced, it goes back to the element with the same id, and a
     text field keeps what the shopper has typed since: the response can land
     mid-typing (min price changed, request in flight, now typing the max). */
  function swapRegions(doc) {
    var active = document.activeElement;
    var activeId = active && active !== document.body ? active.id : '';
    var typed = active && active.tagName === 'INPUT' && active.type !== 'checkbox' ? active.value : null;

    document.querySelectorAll('[data-filter-region]').forEach(function (region) {
      var fresh = doc.querySelector('[data-filter-region="' + region.dataset.filterRegion + '"]');
      if (fresh) region.innerHTML = fresh.innerHTML;
    });

    if (activeId && !document.body.contains(active)) {
      var next = document.getElementById(activeId);
      if (next) {
        if (typed !== null) next.value = typed;
        next.focus({ preventScroll: true });
      }
    }

    // The sort control is not a region (its listener would go with it), so
    // it follows the response instead — this is what keeps it right on Back.
    var freshSort = doc.querySelector('[data-sort-select]');
    if (sortSelect && freshSort) {
      sortSelect.value = freshSort.value;
      syncSortLabel();
    }
  }

  function syncSortLabel() {
    if (!sortSelect || !sortLabel) return;
    var option = sortSelect.options[sortSelect.selectedIndex];
    if (option) sortLabel.textContent = option.text;
  }

  /* After a change further down the page, bring the top of the results back
     into view (just under the nav and, on a phone, the sticky bar). Moves
     #windowInner, which is what scrolls (theme.css §6). */
  function revealResults() {
    var scroller = scrollContainer();
    var main = shell.querySelector('.collection-main');
    if (!scroller || !main) return;
    var nav = document.querySelector('.nav');
    var offset = (nav ? nav.offsetHeight : 0) + 8;
    var top = main.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    if (top < offset) scroller.scrollTop += top - offset;
  }

  function render(search, push) {
    clearTimeout(timer);
    if (search === lastSearch) return;
    lastSearch = search;

    var url = window.location.pathname + (search ? '?' + search : '');
    if (!sectionId || !window.fetch || !window.DOMParser) {
      window.location.assign(url);
      return;
    }

    if (controller) controller.abort();
    controller = window.AbortController ? new AbortController() : null;
    var id = ++requestId;
    setBusy(true);

    fetch(sectionUrl(sectionId, url), controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error('collection section ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // A newer change has already been sent; its response is the one to show.
        if (id !== requestId) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('[data-filter-region="results"]')) {
          throw new Error('collection section markup missing [data-filter-region]');
        }
        swapRegions(doc);
        if (push) window.history.pushState({ collectionFilters: true }, '', url);
        setBusy(false);
        controller = null;
        if (overlay && overlay.isOpen()) revealOnClose = true;
        else revealResults();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location.assign(url);
      });
  }

  form.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('[data-filter-input]')) schedule();
  });

  /* "Show N results" in the sheet, or Enter in a price box. Results are
     already live, so this sends whatever is still pending and closes. */
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    render(buildSearch(), true);
    if (overlay && mq.matches) overlay.close();
  });

  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      syncSortLabel();
      render(buildSearch(), true);
    });
  }

  // Active-filter chips, "clear all" and the sheet's reset link. Delegated:
  // they live inside regions that are replaced on every render.
  document.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('[data-filter-link]') : null;
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var target = new URL(link.href, window.location.href);
    if (target.pathname !== window.location.pathname) return;
    e.preventDefault();
    render(target.search.replace(/^\?/, ''), true);
  });

  window.addEventListener('popstate', function () {
    render(window.location.search.replace(/^\?/, ''), false);
  });

  if (!sidebar) return;

  overlay = createOverlay({
    panel: sidebar,
    overlayEl: document.querySelector('[data-filters-overlay]'),
    trigger: openBtn,
    initialFocus: '[data-filters-close]',
    onClose: function () {
      if (!revealOnClose) return;
      revealOnClose = false;
      revealResults();
    },
  });
  if (!overlay) return;

  /* The panel is a static column at desktop widths and only becomes a
     modal sheet below the 900px breakpoint — inert/focus-trap must not
     apply while it's just part of the page. */
  function syncMode() {
    if (mq.matches) {
      if (!overlay.isOpen()) sidebar.setAttribute('inert', '');
    } else {
      overlay.close();
      sidebar.removeAttribute('inert');
    }
  }
  if (mq.addEventListener) mq.addEventListener('change', syncMode);
  else mq.addListener(syncMode); // Safari < 14
  syncMode();

  if (openBtn) openBtn.addEventListener('click', function () { overlay.open(); });
  document.querySelectorAll('[data-filters-close]').forEach(function (btn) {
    btn.addEventListener('click', function () { overlay.close(); });
  });
}

/* ── Accordions (product description, notice, size guide, shipping) ── */
function initAccordions() {
  document.querySelectorAll('[data-accordion-trigger]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.closest('.accordion-item');
      if (!item) return;
      var isOpen = item.classList.contains('is-open');
      item.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });
}

/* ── Product recommendations ───────────────────────────────────
   Fetched client-side via Shopify's native recommendations endpoint so the
   algorithm's result isn't baked into the cached page.
*/
function initProductRecommendations() {
  var el = document.querySelector('[data-product-recommendations]');
  if (!el) return;
  var productId = el.dataset.productRecommendations;
  var sectionId = el.dataset.sectionId;
  if (!productId || !sectionId) return;

  var url = rootUrl() + 'recommendations/products?product_id=' +
    encodeURIComponent(productId) + '&limit=4&section_id=' + encodeURIComponent(sectionId);

  fetch(url)
    .then(function (res) { return res.text(); })
    .then(function (html) {
      var fresh = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('[data-product-recommendations]');
      if (fresh && fresh.innerHTML.trim().length) el.innerHTML = fresh.innerHTML;
    })
    .catch(function () {});
}


/* ── Predictive search panel ───────────────────────────────────
   Opens from any [data-search-open] control (header icon, mobile menu) and
   queries Shopify's own /search/suggest.json as the shopper types.

   Progressive enhancement: the triggers are real <a href="/search"> links
   and the panel contains a real GET form, so with JS off (or if this init
   throws) search still works — you just land on the results page instead of
   seeing suggestions inline.
*/
var SEARCH_DEBOUNCE_MS = 220;
var searchOverlayCtl = null;

function initSearchPanel() {
  var panel = document.querySelector('[data-search-panel]');
  /* Deliberately NOT gated on a trigger existing. The header's search icon is
     a merchant toggle (header settings > show_search), and requiring one
     meant that turning it off also killed the ⌘K shortcut and the search
     page's own field handover — the whole palette went dead with the panel
     markup still sitting in the DOM. */
  var triggers = document.querySelectorAll('[data-search-open]');
  if (!panel) return;

  var input = panel.querySelector('[data-search-input]');
  var results = panel.querySelector('[data-search-results]');
  var idle = panel.querySelector('[data-search-idle]');
  var status = panel.querySelector('[data-search-status]');
  var clearBtn = panel.querySelector('[data-search-clear]');
  var form = panel.querySelector('[data-search-form]');
  var overlayEl = document.querySelector('[data-search-overlay]');
  if (!input || !results || !form) return;

  var timer = null;
  var lastQuery = '';
  // Every in-flight request carries a token; only the newest one is allowed
  // to paint. Without this, a slow early keystroke could land after a fast
  // later one and overwrite fresher results with staler ones.
  var requestToken = 0;

  searchOverlayCtl = createOverlay({
    panel: panel,
    overlayEl: overlayEl,
    initialFocus: '[data-search-input]',
    onOpen: function () {
      // Deliberately not clearing the field: reopening the panel after a
      // mistyped query should let you fix it, not start over.
      if (input.value.trim()) run(input.value);
      else showIdle();
    },
  });

  triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      // Let modified clicks (new tab/window) fall through to the real href.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      searchOverlayCtl.open();
    });
  });

  /* The search RESULTS page has its own real form (it must keep working with
     JS off). With JS on, touching that field hands over to the palette
     pre-filled with the current query, so there is one search experience on
     the site rather than two that look and behave differently. */
  document.querySelectorAll('[data-search-inline]').forEach(function (field) {
    function handover(e) {
      if (e) e.preventDefault();
      if (searchOverlayCtl.isOpen()) return;
      if (field.value && !input.value) input.value = field.value;
      field.blur();
      searchOverlayCtl.open();
    }
    field.addEventListener('focus', handover);
    field.addEventListener('click', handover);
  });

  /* ⌘K / Ctrl-K opens the palette from anywhere, "/" does too unless the
     shopper is already typing into something. */
  document.addEventListener('keydown', function (e) {
    var isPaletteKey = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
    var isSlash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
    if (!isPaletteKey && !isSlash) return;
    if (searchOverlayCtl.isOpen()) return;
    var el = document.activeElement;
    var typing = el && (el.isContentEditable ||
      /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ''));
    if (isSlash && typing) return;
    e.preventDefault();
    searchOverlayCtl.open();
  });

  panel.querySelectorAll('[data-search-close]').forEach(function (btn) {
    btn.addEventListener('click', function () { searchOverlayCtl.close(); });
  });
  if (overlayEl) overlayEl.addEventListener('click', function () { searchOverlayCtl.close(); });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      showIdle();
      input.focus();
    });
  }

  input.addEventListener('input', function () {
    var q = input.value;
    if (clearBtn) clearBtn.hidden = !q;
    if (timer) clearTimeout(timer);
    if (!q.trim()) { showIdle(); return; }
    timer = setTimeout(function () { run(q); }, SEARCH_DEBOUNCE_MS);
  });

  // ↑/↓ move through suggestions; Enter on a highlighted one follows it,
  // Enter with nothing highlighted submits the form to the results page.
  input.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    var options = Array.prototype.slice.call(results.querySelectorAll('[data-search-option]'));
    if (!options.length) return;
    var current = results.querySelector('[data-search-option].is-active');
    var idx = options.indexOf(current);

    if (e.key === 'Enter') {
      if (current) { e.preventDefault(); current.click(); }
      return;
    }
    e.preventDefault();
    var next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
    if (next < 0) next = options.length - 1;
    if (next >= options.length) next = 0;
    setActive(options, options[next]);
  });

  function setActive(options, target) {
    options.forEach(function (o) {
      o.classList.toggle('is-active', o === target);
      o.setAttribute('aria-selected', o === target ? 'true' : 'false');
    });
    if (target) {
      input.setAttribute('aria-activedescendant', target.id || '');
      if (target.scrollIntoView) target.scrollIntoView({ block: 'nearest' });
    }
  }

  function showIdle() {
    lastQuery = '';
    results.innerHTML = '';
    results.hidden = true;
    if (idle) idle.hidden = false;
    input.setAttribute('aria-expanded', 'false');
    setStatus('');
  }

  function setStatus(text) {
    if (status) status.textContent = text;
  }

  function run(q) {
    q = q.trim();
    if (!q || q === lastQuery) return;
    lastQuery = q;
    var token = ++requestToken;

    var url =
      rootUrl().replace(/\/$/, '') +
      '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection,page,article' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,vendor';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('search ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (token !== requestToken) return;
        render(data && data.resources && data.resources.results, q);
      })
      .catch(function () {
        if (token !== requestToken) return;
        // A failed suggest call must not strand the shopper: fall back to a
        // plain link through to the real results page.
        results.hidden = false;
        if (idle) idle.hidden = true;
        results.innerHTML = '';
        results.appendChild(viewAllLink(q));
      });
  }

  function viewAllLink(q) {
    var a = document.createElement('a');
    a.className = 'search-panel__all';
    a.href = form.action + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
    a.setAttribute('data-search-option', '');
    a.setAttribute('role', 'option');
    a.setAttribute('aria-selected', 'false');
    a.textContent = (results.dataset.allText || 'Alle Ergebnisse für') + ' “' + q + '”';
    return a;
  }

  function render(res, q) {
    results.innerHTML = '';
    if (idle) idle.hidden = true;
    results.hidden = false;

    var groups = [
      { key: 'products', label: results.dataset.labelProducts },
      { key: 'collections', label: results.dataset.labelCollections },
      { key: 'pages', label: results.dataset.labelPages },
      { key: 'articles', label: results.dataset.labelArticles },
    ];

    var total = 0;
    var optionIndex = 0;

    groups.forEach(function (g) {
      var list = (res && res[g.key]) || [];
      if (!list.length) return;
      total += list.length;

      var section = document.createElement('div');
      section.className = 'search-panel__group';

      var heading = document.createElement('p');
      heading.className = 'search-panel__group-title';
      heading.textContent = g.label || g.key;
      section.appendChild(heading);

      list.forEach(function (item) {
        section.appendChild(buildOption(item, g.key, optionIndex++));
      });
      results.appendChild(section);
    });

    if (!total) {
      var empty = document.createElement('p');
      empty.className = 'search-panel__empty';
      empty.textContent = (results.dataset.noResults || 'Keine Ergebnisse').replace('[terms]', q);
      results.appendChild(empty);
    }

    results.appendChild(viewAllLink(q));
    input.setAttribute('aria-expanded', 'true');
    setStatus((results.dataset.countText || '[count]').replace('[count]', total));
  }

  /* suggest.json returns `price` as a bare major-unit string ("49.99"), not
     as formatted money — it is the one price in this theme with no
     server-rendered form available (see README "JS architecture").

     Driven by the shop's own money_format rather than Intl.NumberFormat:
     this shop's format is "€{{amount_with_comma_separator}}" → "€49,99",
     whereas Intl with de-DE produces "49,99 €". Every other price on the
     site comes from the `money` filter, so Intl would have made the search
     panel the only place showing a different currency layout. */
  function formatSuggestPrice(raw) {
    if (raw === null || raw === undefined || raw === '') return '';
    var amount = parseFloat(raw);
    if (isNaN(amount)) return '';

    var fmt = results.dataset.moneyFormat || '';
    var token = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!token) return String(raw);

    var name = token[1];
    var noDecimals = name.indexOf('no_decimals') !== -1;
    var commaSeparator = name.indexOf('comma_separator') !== -1;
    var apostropheSeparator = name.indexOf('apostrophe_separator') !== -1;

    var thousands = commaSeparator ? '.' : apostropheSeparator ? "'" : ',';
    var decimal = commaSeparator ? ',' : '.';

    var parts = amount.toFixed(noDecimals ? 0 : 2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return fmt.replace(token[0], parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0]);
  }

  function buildOption(item, kind, i) {
    var a = document.createElement('a');
    a.className = 'search-panel__option';
    a.href = item.url;
    a.id = 'search-option-' + i;
    a.setAttribute('data-search-option', '');
    a.setAttribute('role', 'option');
    a.setAttribute('aria-selected', 'false');

    if (kind === 'products') {
      var thumb = document.createElement('span');
      thumb.className = 'search-panel__thumb';
      var src = (item.featured_image && item.featured_image.url) || item.image;
      if (src) {
        var img = document.createElement('img');
        // suggest.json hands back full-resolution CDN URLs (2750px jerseys
        // here); ask the CDN for a thumbnail instead of pulling six of those
        // on every keystroke.
        img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
        img.alt = '';
        img.loading = 'lazy';
        img.width = 44;
        img.height = 44;
        thumb.appendChild(img);
      }
      a.appendChild(thumb);
    }

    var text = document.createElement('span');
    text.className = 'search-panel__option-text';

    var title = document.createElement('span');
    title.className = 'search-panel__option-title';
    // textContent, never innerHTML: suggest.json echoes the shopper's own
    // query back inside these strings.
    title.textContent = item.title;
    text.appendChild(title);

    var meta = kind === 'products' ? (formatSuggestPrice(item.price) || item.vendor) : null;
    if (meta) {
      var sub = document.createElement('span');
      sub.className = 'search-panel__option-meta';
      sub.textContent = meta;
      text.appendChild(sub);
    }

    a.appendChild(text);

    if (kind === 'products' && item.available === false) {
      var badge = document.createElement('span');
      badge.className = 'search-panel__option-flag';
      badge.textContent = results.dataset.soldOut || '';
      if (badge.textContent) a.appendChild(badge);
    }

    a.addEventListener('mouseenter', function () {
      setActive(Array.prototype.slice.call(results.querySelectorAll('[data-search-option]')), a);
    });
    return a;
  }
}


/* ── Auction: bank-transfer hold notice ────────────────────────
   Reveals [data-auction-wire-notice] once the live bid passes the threshold
   the merchant set on the section (see main-product.liquid). This is decided
   in the browser, not in Liquid, for the obvious reason: the winning amount
   doesn't exist at render time.

   The amount is read out of Auction Plus's own rendered DOM
   (.current-display-amount), the same way initAppOptions reads the options
   app's prices — the app owns the number, the theme just watches it. The
   theme's fallback auction card ([data-auction-price], see
   snippets/auction-info.liquid) is checked first for shops running without
   the app.
*/

/* NOT parseMoney(): that takes the money format the writer used, and the
   auction app renders its own ("€80.00") rather than the shop's ("80,00 €"),
   so there is no format to hand it. The decimal mark is therefore sniffed
   from the string — a separator followed by exactly two digits at the very
   end is a decimal mark, anything else is grouping. That resolves the only
   case that actually collides: "€1.234" (1234 in a German shop) vs "€1.23".
   Returns null, not 0, for "no number here" — 0 is a real amount and would
   read as "below threshold" instead of "unknown". */
function auctionAmountCents(text) {
  var str = String(text == null ? '' : text).replace(/[^\d.,]/g, '');
  if (!/\d/.test(str)) return null;
  var frac = /[.,](\d{2})$/.exec(str);
  var whole = (frac ? str.slice(0, -3) : str).replace(/\D/g, '');
  return parseInt(whole || '0', 10) * 100 + (frac ? parseInt(frac[1], 10) : 0);
}

function initAuctionNotices() {
  var notice = document.querySelector('[data-auction-wire-notice]');
  if (!notice) return;

  var threshold = parseInt(notice.getAttribute('data-threshold-cents'), 10);
  if (!(threshold > 0)) return;

  function currentCents() {
    // The theme's own card first (it prints the shop's format via Liquid),
    // then the app's widget. `.auction-container` is preferred over a bare
    // `.current-display-amount` so the in-page figure wins over the copy the
    // app repeats inside its bid modal — they agree, but the in-page one is
    // the one that is definitely present.
    // Queried from `document`, not from the product column: where exactly the
    // app mounts its widget is the app's business, not something the theme
    // should assume.
    var el =
      document.querySelector('[data-auction-price]') ||
      document.querySelector('.auction-container .current-display-amount') ||
      document.querySelector('.current-display-amount');
    return el ? auctionAmountCents(el.textContent) : null;
  }

  function sync() {
    var cents = currentCents();
    if (cents === null) return;
    notice.hidden = cents < threshold;
  }

  sync();

  // The widget is a web component the app injects after this script runs, and
  // it rewrites the amount in place on every new bid — so both the arrival of
  // the element and later edits to its text have to be watched. document.body
  // for the same reason the query above is unscoped. The auction countdown
  // ticking once a second keeps this firing steadily; that is fine, it is
  // rAF-coalesced down to three querySelectors per frame at worst, and the
  // whole function only exists on pages that render the notice at all.
  if (typeof MutationObserver === 'undefined') return;
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      sync();
    });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
}

/* ── Qikify / Tepo product options ─────────────────────────────
   The app renders its own option UI into #tepo-options-container and styles
   every element with INLINE styles from its admin settings (white chip with
   a 1px black border, black chip when selected). Inline styles beat any
   stylesheet, so theme.css restyles them with `!important` — see the
   .fpob-* block there.

   That creates one problem this function exists to solve: the app marks the
   selected value ONLY by mutating that element's inline background-color.
   Once theme.css overrides the colours, selected and unselected become
   indistinguishable. So rather than styling on the app's colours, we read
   them, work out which element is selected, and expose that as a class the
   stylesheet can target.

   The selected colour is not hardcoded: it is whichever inline background is
   in the MINORITY across the whole option UI. Merchants can change both
   colours in the app's admin, and a per-group majority would tie in a
   two-value group where one is selected — a page-wide majority does not.
*/
function initAppOptions() {
  /* Observe the whole product form, NOT #tepo-options-container.
     That element exists on every PDP but the app does not necessarily fill
     it — on this shop it injects .fpob-main-container into
     .product-form__buy-buttons instead (which is why theme.css has a
     `.product-form__buy-buttons > .fpob-main-container` rule). Reaching for
     #tepo-options-container first meant observing a permanently empty div:
     the first classification ran, and then nothing ever fired again, so the
     selected styling froze on whatever was selected at page load.
     The form contains every slot the app might use. */
  var container = document.querySelector('[data-product-form]') ||
    document.getElementById('tepo-options-container');
  if (!container) return;

  var SELECTED_CLASS = 'is-app-selected';
  var scheduled = false;

  /* Marks the elements whose inline background differs from the majority's.
     `strict`: on a tie mark nothing rather than guess which colour rests. */
  function markMinority(list, strict) {
    var items = Array.prototype.slice.call(list);
    if (items.length < 2) return;

    var counts = {};
    items.forEach(function (el) {
      var bg = el.style.backgroundColor || '';
      counts[bg] = (counts[bg] || 0) + 1;
    });

    var restingColour = null;
    var best = -1;
    var tied = false;
    Object.keys(counts).forEach(function (bg) {
      if (counts[bg] > best) { best = counts[bg]; restingColour = bg; tied = false; }
      else if (counts[bg] === best) { tied = true; }
    });

    // Every element sharing one colour means nothing is selected yet.
    if (best === items.length || (strict && tied)) {
      items.forEach(function (el) { el.classList.remove(SELECTED_CLASS); });
      return;
    }

    items.forEach(function (el) {
      var bg = el.style.backgroundColor || '';
      el.classList.toggle(SELECTED_CLASS, bg !== restingColour);
    });
  }

  function classify() {
    // Chips: counted page-wide, so a two-value group with one picked
    // still has a clear majority.
    markMinority(document.querySelectorAll('.fpob-btn-value-element'));
    /* Dropdown lists (the player picker): the app marks the picked item
       the same way, with an inline grey that sat under the theme's light
       --ink text in dark mode. Counted per list, since its colours are not
       the chips'; strict, because a two-item list would otherwise be a
       coin toss (unmarked, the text is still readable). */
    Array.prototype.forEach.call(
      document.querySelectorAll('.fpob-drd-dropdown-menu'),
      function (menu) { markMinority(menu.querySelectorAll('.fpob-drd-item'), true); }
    );
  }

  /* ── What the app is charging ───────────────────────────────
     Every option group the merchant priced carries a price span in its
     own label — `fpob-btn-price-<optionId>` and the equivalents for the
     other widget types — which the app fills with its running total for
     that group (" (+ €10,00)") and empties again on deselect.

     Summing those spans is deliberately preferred over re-deriving the
     total from window.fpob_variant_options[variantId].options[].items[]
     .price. The app already knows which of its ten-odd widget types count
     as "selected", how a multi-select group adds up, and which
     conditionally-shown options are currently even in play; re-deriving
     that from the option definitions would be a second implementation of
     the app's own rules, silently drifting from it on every app update.

     Parsed with the app's OWN money format, not the theme's: it is the
     app that wrote these strings. */
  var PRICE_SPAN = /(^|\s)fpob-[a-z]*-price-\d+(\s|$)/;
  var lastAddonCents = null;

  function addonCents() {
    var format = (window.fpob_settings && window.fpob_settings.moneyFormat) || '';
    var total = 0;
    Array.prototype.forEach.call(
      document.querySelectorAll('.fpob-main-container [class*="-price-"]'),
      function (el) {
        if (typeof el.className !== 'string' || !PRICE_SPAN.test(el.className)) return;
        /* Skip the app's "HTML" option type. Its value is merchant-authored
           markup that can contain a verbatim copy of another option's
           markup, price span and all — this shop's jerseys do exactly that
           (the "Variante" Fan/Kinder switcher). The app never fills a copy,
           but one pasted with text already in it would be counted twice. */
        if (el.closest('.fpob--markup')) return;
        total += parseMoney(el.textContent, format);
      }
    );
    return total;
  }

  function syncPrice() {
    var cents = addonCents();
    if (cents === lastAddonCents) return;
    lastAddonCents = cents;
    document.dispatchEvent(
      new CustomEvent(OPTION_PRICE_EVENT, { detail: { cents: cents } })
    );
  }

  function pass() {
    scheduled = false;
    classify();
    syncPrice();
  }

  /* setTimeout, not requestAnimationFrame: rAF does not run while the page
     is not being rendered (a background tab, for one), which would leave the
     scheduled flag stuck and the selected styling frozen on whatever was
     selected when the tab was last visible. A 0ms timer still coalesces the
     burst of style writes the app makes per selection.

     Neither half of the pass can retrigger the observer: classify() only
     writes `class`, and syncPrice() writes nothing at all — it announces a
     number, and the price elements that react to it live above the form,
     outside the observed subtree. */
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(pass, 0);
  }

  /* The app mutates style attributes in place (it does not re-render the
     nodes), and it renders asynchronously after this script runs — so the
     observer covers both the first paint and every later selection.
     Coalescing stops a burst of style writes causing a burst of passes.

     characterData is here for the price spans: the app rewrites a group's
     surcharge text on every selection, and whether that arrives as a
     replaced text node (childList) or an edited one (characterData) is an
     app-internal detail — on a button group it was measured as childList,
     which the observer already saw. Watching both costs nothing and stops
     the surcharge silently going unnoticed on a widget type that happens
     to write its text the other way. */
  var observer = new MutationObserver(schedule);
  observer.observe(container, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['style'],
  });

  schedule();
}
